import {
  AlertOctagon,
  AlertTriangle,
  BookOpen,
  BrainCircuit,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  ImagePlus,
  Info,
  Loader2,
  Pencil,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTaskTypeLabel, TaskType } from '../../../../../../db/api/task-types/task-main.js';
import {
  type BoundingBoxContentType,
  normalizeContentType,
  shouldShowOcrText,
} from '../../../../../../shared/bounding-box-content.js';
import { GRAMMAR_TASK_TYPES } from '../../../../../../shared/grammar-grading.js';
import { formatTaskReferenceForDisplay } from '../../../../../../shared/grammar-rules-resolution.js';
import { cn } from '../../../../../lib/utils.js';
import { MarkdownWithMath } from '../../../../markdown/markdown-with-math.js';
import { LatexRichEditor } from '../../../../ui/latex-rich-editor.js';
import { BoxLoadingOverlay } from '../../../shared/box-loading-overlay.js';
import { BoxModeToggle } from '../../grading/components/box-mode-toggle.js';
import type { SaveTarget } from '../../grading/utils.js';
import type { SolutionStateStore } from '../hooks/use-solution-state.js';
import { getGroupLandingTaskIndex, getVisibleGroupTaskIndices, hasZeroContentPoints } from '../utils.js';

/**
 * Renders a solution section (master solution / grading scheme) by its aggregate content type:
 *   - 'text'  -> the appended text as a single block
 *   - 'image' -> every box image stacked below each other
 *   - 'both'  -> the box images stacked, followed by the appended text
 * Editing is handled by the parent (text-only); this is the read-only view.
 */
function SectionContent({
  contentType,
  imageUrls,
  imageIds,
  text,
  emptyLabel,
  imageAlt,
  proseClassName,
  onDeleteImage,
  onDropImages,
  isUploading,
}: {
  contentType: BoundingBoxContentType | undefined;
  imageUrls: string[];
  imageIds: string[];
  text: string;
  emptyLabel: string;
  imageAlt: string;
  proseClassName: string;
  onDeleteImage?: (imageIds: string[], index: number) => void;
  onDropImages?: (files: File[]) => void;
  isUploading?: boolean;
}) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const type = normalizeContentType(contentType);
  const showImages = type !== 'text' && imageUrls.length > 0;
  const showText = (type === 'text' || type === 'both') && Boolean(text && text.trim().length > 0);

  const body =
    !showImages && !showText ? (
      <span className="text-slate-400 italic">{emptyLabel}</span>
    ) : (
      <>
        {showImages &&
          imageUrls.map((url, index) => (
            <div key={`section-img-${index}`} className="relative mx-auto group inline-block">
              <img src={url} alt={imageAlt} className="max-w-full h-auto rounded shadow-sm object-contain" />

              {/* Delete Button (visible on hover) */}
              {onDeleteImage && (
                <button
                  type="button"
                  onClick={() => onDeleteImage(imageIds, index)}
                  className="absolute top-2 right-2 p-1.5 bg-white/90 dark:bg-slate-800/90 text-slate-600 dark:text-slate-300 hover:bg-red-500 hover:text-white dark:hover:bg-red-500 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all duration-200"
                  title="Delete image"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
        {showText && (
          <div className={proseClassName}>
            <MarkdownWithMath>{text}</MarkdownWithMath>
          </div>
        )}
      </>
    );

  // Sections without an upload handler (e.g. the read-only description) stay plain.
  if (!onDropImages) {
    return <div className="flex flex-col gap-3">{body}</div>;
  }

  return (
    <div
      onDragOver={e => {
        e.preventDefault();
        if (!isDragging) setIsDragging(true);
      }}
      onDragLeave={e => {
        e.preventDefault();
        // Only clear when leaving the dropzone itself, not when moving over child elements.
        if (e.currentTarget === e.target) setIsDragging(false);
      }}
      onDrop={e => {
        e.preventDefault();
        setIsDragging(false);
        if (isUploading) return;
        const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
        if (files.length > 0) onDropImages(files);
      }}
      className={cn(
        'relative flex flex-col gap-3 rounded-md transition-colors min-h-[80px]',
        isDragging && 'outline-dashed outline-2 outline-blue-400 outline-offset-2 bg-blue-50/40 dark:bg-blue-900/10',
      )}
    >
      {body}

      {(isDragging || isUploading) && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-md bg-white/70 dark:bg-slate-900/70 text-sm text-slate-600 dark:text-slate-300 pointer-events-none">
          {isUploading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              <span>{t('Canvas.msgs.uploadingImage', { defaultValue: 'Uploading…' })}</span>
            </>
          ) : (
            <>
              <ImagePlus size={20} />
              <span>{t('Canvas.msgs.dropImageToAdd', { defaultValue: 'Drop image to add' })}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatTaskLabel(taskNumber: string, translate: (key: string, options?: { defaultValue?: string }) => string) {
  const normalizedTaskNumber = taskNumber.trim();
  if (!normalizedTaskNumber) return taskNumber;

  const segments = normalizedTaskNumber.split('.');
  const lastSegment = segments[segments.length - 1];
  if (!lastSegment) return normalizedTaskNumber;

  const taskType = Object.values(TaskType).includes(lastSegment as TaskType) ? (lastSegment as TaskType) : undefined;
  if (!taskType || taskType === TaskType.DEFAULT || taskType === TaskType.TASK_FRAME) return normalizedTaskNumber;

  segments[segments.length - 1] = getTaskTypeLabel(taskType, translate);
  return segments.join('.');
}

function renderTaskMainLabel(
  taskNumber: string,
  translate: (key: string, options?: { defaultValue?: string }) => string,
) {
  return translate('Canvas.msgs.taskMain', { main: formatTaskLabel(taskNumber, translate) } as any);
}

// The nav row gives things up in this order as it runs out of room. Which stage is active is decided
// by measuring the real layout (see useLayoutEffect below) — never by width breakpoints — so it
// collapses exactly when the zones stop fitting side by side, whatever the widths happen to be.
const NAV_COLLAPSE_STAGES = [
  'nothing collapsed',
  'uncertainty label wraps to 2 lines',
  'uncertainty label drops to icon + count',
  'task label drops its "Aufgabe" prefix',
] as const;
const LAST_NAV_COLLAPSE_STAGE = NAV_COLLAPSE_STAGES.length - 1;

export function TaskNavigation({ store }: { store: SolutionStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions, refs } = store;

  const navRowRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ stage: 0, pass: 0 });
  const collapseStage = fit.stage;
  const measuredRowWidthRef = useRef(0);

  // A change in available width goes back to the widest layout; the layout effect below then walks
  // back down. `pass` guarantees a re-render (and re-measure) even when `stage` resets to an already-0
  // value, which a plain setState would otherwise bail out of.
  useEffect(() => {
    const row = navRowRef.current;
    if (!row) return;
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width === measuredRowWidthRef.current) return;
      measuredRowWidthRef.current = width;
      setFit(previous => ({ stage: 0, pass: previous.pass + 1 }));
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  // Deliberately no dependency array: runs after every render, stepping down one stage per render
  // until the row fits — converging before paint. Every zone is content-sized, so as soon as the
  // zones stop fitting side by side the last one's right edge crosses the row's content edge.
  useLayoutEffect(() => {
    const row = navRowRef.current;
    const lastZone = row?.lastElementChild;
    if (!row || !lastZone || collapseStage >= LAST_NAV_COLLAPSE_STAGE) return;
    const style = getComputedStyle(row);
    const contentRightEdge =
      row.getBoundingClientRect().right - parseFloat(style.borderRightWidth) - parseFloat(style.paddingRight);
    // Half a pixel of tolerance for sub-pixel layout rounding, not a threshold.
    if (lastZone.getBoundingClientRect().right > contentRightEdge + 0.5) {
      setFit(previous => ({ stage: previous.stage + 1, pass: previous.pass }));
    }
  });

  const uncertaintyLabelWraps = collapseStage === 1;
  const uncertaintyLabelHidden = collapseStage >= 2;
  const showTaskNamePrefix = collapseStage < 3;

  const navigableIndices = computed.taskGroups.flatMap(group => getVisibleGroupTaskIndices(group));

  const currentNavIndex = navigableIndices.indexOf(computed.actualIndex);

  const prevIndex = currentNavIndex > 0 ? (navigableIndices[currentNavIndex - 1] ?? null) : null;
  const nextIndex =
    currentNavIndex !== -1 && currentNavIndex < navigableIndices.length - 1
      ? (navigableIndices[currentNavIndex + 1] ?? null)
      : null;
  // The row stands for the whole task, so either extracted section justifies colouring it.
  const shouldColorConfidence =
    computed.currentTask?.masterSolutionSource === 'upload' || computed.currentTask?.gradingSchemeSource === 'upload';
  const uncertaintyCount = state.uncertaintyTaskIndices.length;

  const getConfidenceClass = (status: 'low' | 'medium' | 'ok' | undefined, selected: boolean) => {
    if (!shouldColorConfidence || !status) {
      return selected
        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-semibold'
        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700';
    }

    if (selected) {
      return status === 'low'
        ? 'bg-red-50 text-red-600 font-semibold'
        : status === 'medium'
          ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 font-semibold'
          : 'bg-blue-50 text-blue-600 font-semibold';
    }

    return status === 'low'
      ? 'text-red-600 hover:bg-red-50'
      : status === 'medium'
        ? 'text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20'
        : 'text-slate-600 hover:bg-slate-50';
  };

  return (
    <div className="flex items-stretch px-1 gap-3" ref={navRowRef}>
      <div className="relative shrink-0" ref={refs.dropdownRef}>
        <div className="flex items-center gap-1 bg-white dark:bg-card border-2 border-slate-100 dark:border-slate-700 rounded-xl p-1 shadow-sm">
          <button
            className="h-9 w-9 inline-flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-30 transition-colors"
            disabled={prevIndex === null}
            onClick={() => prevIndex !== null && actions.onTaskChange(prevIndex)}
            title={t('Canvas.msgs.prevTask')}
          >
            <ChevronLeft size={18} className="text-slate-600" />
          </button>

          <button
            onClick={() => actions.setIsDropdownOpen(!state.isDropdownOpen)}
            className={cn(
              'flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium text-slate-700 dark:text-slate-300',
              state.isDropdownOpen
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : 'hover:bg-slate-50 dark:hover:bg-slate-700',
            )}
          >
            <span className="whitespace-nowrap">
              {showTaskNamePrefix
                ? renderTaskMainLabel(
                    computed.currentTask?.taskNumber || computed.currentTask?.name || t('Canvas.msgs.task'),
                    t,
                  )
                : formatTaskLabel(
                    computed.currentTask?.taskNumber || computed.currentTask?.name || t('Canvas.msgs.task'),
                    t,
                  )}
            </span>
            <ChevronDown
              size={16}
              className={cn('text-slate-400 transition-transform', state.isDropdownOpen && 'rotate-180')}
            />
          </button>

          <button
            className="h-9 w-9 inline-flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-30 transition-colors"
            disabled={nextIndex === null}
            onClick={() => nextIndex !== null && actions.onTaskChange(nextIndex)}
            title={t('Canvas.msgs.nextTask')}
          >
            <ChevronRight size={18} className="text-slate-600" />
          </button>
        </div>

        {state.isDropdownOpen && (
          <div className="absolute left-0 mt-2 w-full max-h-64 overflow-y-auto bg-white dark:bg-card border border-slate-100 dark:border-slate-700 rounded-xl shadow-xl z-50 py-2 custom-scrollbar ease-in animate-in fade-in slide-in-from-top-2">
            {computed.taskGroups.map(group => {
              const visibleSubTasks = group.subTasks.filter(sub => sub.needsAnswer);
              const shouldShowMainTask = group.mainNeedsAnswer;

              if (!shouldShowMainTask && visibleSubTasks.length === 0) return null;

              return (
                <div key={group.mainNum}>
                  {shouldShowMainTask && (
                    <button
                      className={cn(
                        'w-full text-left px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-1.5',
                        getConfidenceClass(
                          shouldColorConfidence
                            ? state.confidenceStatusMap?.[getGroupLandingTaskIndex(group) ?? 0]
                            : undefined,
                          computed.currentGroup?.mainNum === group.mainNum && computed.currentDotIdx === -1,
                        ),
                      )}
                      onClick={() => {
                        actions.onTaskChange(getGroupLandingTaskIndex(group) ?? 0);
                        actions.setIsDropdownOpen(false);
                      }}
                    >
                      <span>{renderTaskMainLabel(group.mainNum, t)}</span>
                    </button>
                  )}
                  {visibleSubTasks.map(sub => (
                    <button
                      key={sub.flatIndex}
                      className={cn(
                        'w-full text-left px-4 py-2 pl-8 text-sm transition-colors flex items-center gap-1.5',
                        getConfidenceClass(
                          state.confidenceStatusMap?.[sub.flatIndex],
                          computed.actualIndex === sub.flatIndex,
                        ),
                      )}
                      onClick={() => {
                        actions.onTaskChange(sub.flatIndex);
                        actions.setIsDropdownOpen(false);
                      }}
                    >
                      <span>{renderTaskMainLabel(`${group.mainNum}.${sub.subNum}`, t)}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {shouldColorConfidence && uncertaintyCount > 0 && (
        <>
          <div className="grow" aria-hidden />
          <button
            onClick={actions.goToNextUncertainty}
            className="shrink-0 inline-flex items-center gap-2 rounded-xl border-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-2.5 text-xs font-medium text-amber-700 dark:text-amber-400 transition-colors hover:bg-amber-100 dark:hover:bg-amber-900/30 shadow-sm"
            title={t('Canvas.msgs.uncertaintiesLeft', { count: uncertaintyCount })}
          >
            <AlertTriangle size={15} className="shrink-0" />
            {uncertaintyLabelHidden ? (
              <span>{uncertaintyCount}</span>
            ) : (
              // The wrapped stage is bounded in `ch` (a font-relative unit, half the label's own
              // length) rather than pixels, so it breaks into two balanced lines at any font size —
              // the row's own items-stretch keeps the button's height fixed regardless.
              <span
                className={cn(
                  uncertaintyLabelWraps
                    ? 'max-w-[13ch] text-left text-[10px] leading-tight [text-wrap:balance]'
                    : 'whitespace-nowrap',
                )}
              >
                {t('Canvas.msgs.nextUncertainty', { count: uncertaintyCount })}
              </span>
            )}
          </button>
        </>
      )}
    </div>
  );
}

/** Static, read-only highlight for the task's description box(es) on a full exam page — no drag handles,
 * unlike the structure editor's BoxOverlay (which edits boxes) or the grading view's InteractiveBox. */
function TaskExcerptBoxHighlight({ box }: { box: { x: number; y: number; width: number; height: number } }) {
  return (
    <div
      className="absolute z-10 border-2 border-blue-500 bg-blue-500/15 pointer-events-none"
      style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.width}%`, height: `${box.height}%` }}
    />
  );
}

/** Wide-canvas task excerpt view: every exam page, scrollable, with the current task's description
 * box(es) highlighted (a task can have more than one, possibly on different pages) — auto-scrolls to the
 * first one whenever the current task changes. */
function TaskExcerptPages({ store }: { store: SolutionStateStore }) {
  const { t } = useTranslation();
  const { computed } = store;

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const pageContainerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const currentTaskKey = String(computed.currentTask?.id ?? computed.currentTask?.taskNumber ?? computed.actualIndex);
  const boxes = computed.currentTaskDescriptionBoxes;

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || boxes.length === 0) return;

    const firstBox = [...boxes].sort((a, b) => a.pageNumber - b.pageNumber || a.y - b.y)[0];
    if (!firstBox) return;
    const page = pageContainerRefs.current[firstBox.pageNumber];
    if (!page) return;

    const boxAbsoluteY = page.offsetTop + page.offsetHeight * (firstBox.y / 100);
    const scrollTarget = boxAbsoluteY - scrollContainer.clientHeight / 3;
    scrollContainer.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
    // Re-run when the task changes, not on every box re-render (boxes is a new array each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTaskKey]);

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col items-center gap-4 bg-slate-100 dark:bg-slate-900/40"
    >
      {computed.pages.map((pageUrl, pageIndex) => (
        <div
          key={pageIndex}
          ref={el => {
            pageContainerRefs.current[pageIndex] = el;
          }}
          className="relative w-full max-w-xl bg-white dark:bg-slate-800 shadow-md shrink-0"
        >
          <img
            src={pageUrl}
            alt={t('Canvas.msgs.examPageX', { page: pageIndex + 1, defaultValue: `Seite ${pageIndex + 1}` })}
            className="block w-full h-auto select-none"
          />
          {boxes
            .filter(box => box.pageNumber === pageIndex)
            .map((box, boxIndex) => (
              <TaskExcerptBoxHighlight key={boxIndex} box={box} />
            ))}
        </div>
      ))}
    </div>
  );
}

export function TaskContentViewer({ store }: { store: SolutionStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions } = store;
  const currentTask = computed.currentTask;

  if (!currentTask) return null;

  // Wide canvas: show every exam page, scrollable, with the current task's description box(es)
  // highlighted, instead of only the pre-cropped excerpt image(s) — falls back to the cropped view
  // when no page images are available (e.g. exam has no blank pages) or the task has no boxes to locate.
  const showFullPages = !state.isCanvasNarrow && computed.pages.length > 0 && computed.currentTaskDescriptionBoxes.length > 0;

  return (
    <div
      className={cn(
        'flex flex-col flex-1 min-h-0 bg-white dark:bg-card rounded-xl border-2 border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden',
      )}
    >
      <div className="flex items-center gap-2 bg-slate-50 dark:bg-secondary/50 px-5 py-3 border-b border-slate-100 dark:border-slate-700 shrink-0">
        <BookOpen size={18} className="text-slate-500 dark:text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t('Canvas.msgs.taskExcerpt')}</h3>
        <span className="ml-auto text-xs font-medium text-slate-400 dark:text-slate-500 bg-white dark:bg-secondary px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700">
          {renderTaskMainLabel(currentTask.taskNumber || currentTask.name, t)}
        </span>
        {!showFullPages && state.resolvedImageUrls.length > 1 && (
          <div className="flex items-center space-x-1 ml-2">
            <button
              className="p-1 hover:bg-slate-100 rounded-md disabled:opacity-30 transition-colors"
              disabled={state.currentImageIndex === 0}
              onClick={() => actions.setCurrentImageIndex(i => i - 1)}
            >
              <ChevronLeft size={14} className="text-slate-600" />
            </button>
            <span className="text-xs text-slate-500 px-1">
              {state.currentImageIndex + 1}/{state.resolvedImageUrls.length}
            </span>
            <button
              className="p-1 hover:bg-slate-100 rounded-md disabled:opacity-30 transition-colors"
              disabled={state.currentImageIndex === state.resolvedImageUrls.length - 1}
              onClick={() => actions.setCurrentImageIndex(i => i + 1)}
            >
              <ChevronRight size={14} className="text-slate-600" />
            </button>
          </div>
        )}
      </div>
      {showFullPages ? (
        <TaskExcerptPages store={store} />
      ) : (
        // `items-start`, not `items-center` — a crop taller than the panel (a big mapping/gap-text task's
        // whole-task description, easily 3x an average task's) would otherwise overflow evenly above AND
        // below the visible box, so the scrollable area's default `scrollTop: 0` opens on the MIDDLE of
        // the image rather than its actual top, hiding the heading above the fold with nothing to indicate
        // scrolling up would reveal it. Starting at the top is also just the natural place to open a crop
        // — matches every other scrollable region here, none of which centers overflowing content.
        <div className="flex-1 flex items-start justify-center p-4 overflow-auto">
          {state.resolvedImageUrls.length > 0 ? (
            <img
              src={state.resolvedImageUrls[state.currentImageIndex]}
              alt={t('Canvas.msgs.taskExcerpt')}
              className="max-w-full h-auto rounded shadow-sm object-contain"
            />
          ) : currentTask.extractedText ? (
            <div className="w-full p-5 overflow-y-auto text-slate-700 dark:text-slate-300 text-sm leading-relaxed custom-scrollbar prose prose-sm max-w-none dark:prose-invert">
              <MarkdownWithMath>{currentTask.extractedText}</MarkdownWithMath>
            </div>
          ) : (
            <span className="text-slate-400 italic">{t('Canvas.msgs.noImageAvailable')}</span>
          )}
        </div>
      )}
    </div>
  );
}

export function MasterSolutionPanel({ store }: { store: SolutionStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions } = store;
  const currentTask = computed.currentTask;

  if (!currentTask) return null;

  // Per task, not per exam: an uploaded master solution can be completed by the generation, so the exam-level
  // generation status and upload flag both answer the wrong question for an individual task.
  const isAiGenerated = currentTask.masterSolutionSource === 'generated';
  // An image-classified master solution has no text to edit — show the image only, uneditable.
  const isImageMasterSolution = !shouldShowOcrText(currentTask.masterSolutionContentType);
  // Confidence exists only where an extraction produced it, so it is shown only for THIS section of THIS
  // task. The exam-level upload flags this replaced (since removed) also coloured the master solution when
  // merely the grading scheme had been uploaded.
  const shouldColorConfidence = currentTask.masterSolutionSource === 'upload';
  const panelStatus = shouldColorConfidence
    ? state.masterSolutionConfidenceStatusMap?.[computed.actualIndex]
    : undefined;
  const panelShellCls =
    panelStatus === 'low'
      ? 'bg-red-50/40 border-red-400'
      : panelStatus === 'medium'
        ? 'bg-amber-50/40 border-amber-300'
        : 'bg-white dark:bg-card border-slate-100 dark:border-slate-700';
  const panelHeaderCls =
    panelStatus === 'low'
      ? 'bg-red-100/70 border-red-200'
      : panelStatus === 'medium'
        ? 'bg-amber-100/70 border-amber-200'
        : 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900';
  const panelTitleCls =
    panelStatus === 'low'
      ? 'text-red-700 dark:text-red-400'
      : panelStatus === 'medium'
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-blue-800 dark:text-blue-400';
  const iconCls =
    panelStatus === 'low' ? 'text-red-600' : panelStatus === 'medium' ? 'text-amber-600' : 'text-blue-600';
  const pencilBtnCls =
    panelStatus === 'low'
      ? 'text-red-700 dark:text-red-400 bg-white dark:bg-secondary border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300'
      : panelStatus === 'medium'
        ? 'text-amber-700 dark:text-amber-400 bg-white dark:bg-secondary border border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300'
        : 'text-blue-700 dark:text-blue-400 bg-white dark:bg-secondary border border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300';
  const saveBtnCls =
    panelStatus === 'low'
      ? 'bg-red-600 hover:bg-red-700'
      : panelStatus === 'medium'
        ? 'bg-amber-600 hover:bg-amber-700'
        : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className={cn('flex flex-col flex-1 min-h-0 rounded-xl border-2 shadow-sm overflow-hidden', panelShellCls)}>
      <div
        className={cn(
          '@container flex items-center justify-between px-1 h-11 border-b shrink-0 gap-0.5',
          panelHeaderCls,
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <BrainCircuit size={18} className={cn(iconCls, 'shrink-0')} />
          <h3
            className={cn(
              'min-w-0 font-semibold overflow-hidden text-ellipsis whitespace-nowrap text-[clamp(9px,3.5cqw,14px)] leading-[clamp(10px,5cqw,20px)]',
              panelTitleCls,
            )}
          >
            {t('Canvas.msgs.masterSolution')}
          </h3>

          {isAiGenerated && (
            <div
              title={t('Canvas.msgs.aiGeneratedProposalTooltip')}
              className="flex items-center justify-center bg-blue-100 p-1 rounded-full text-blue-600 shadow-sm shrink-0"
            >
              <Sparkles size={14} />
              <div className="absolute bottom-full mb-2 hidden group-hover:block w-max px-2.5 py-1 text-xs font-medium text-white bg-slate-800 rounded-md shadow-lg z-50 animate-in fade-in zoom-in-95 duration-100">
                {t('Canvas.msgs.aiGeneratedProposal')}
              </div>
            </div>
          )}
        </div>
        {!state.isEditingMasterSolution ? (
          <div className="flex items-center gap-2 shrink-0">
            {(panelStatus === 'low' || panelStatus === 'medium') && (
              <button
                onClick={actions.handleApproveMasterSolution}
                disabled={computed.isMasterSolutionRefining}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed',
                  panelStatus === 'low'
                    ? 'text-rose-700 dark:text-rose-400 bg-white dark:bg-secondary border border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/20'
                    : 'text-amber-700 dark:text-amber-400 bg-white dark:bg-secondary border border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20',
                )}
              >
                {panelStatus === 'low' ? <AlertOctagon size={14} /> : <AlertTriangle size={14} />}
                <span className="hidden @[21rem]:inline">{t('Canvas.msgs.review')}</span>
              </button>
            )}
            {!isImageMasterSolution && (
              <button
                onClick={() => actions.setIsEditingMasterSolution(true)}
                disabled={computed.isMasterSolutionRefining}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-sm',
                  pencilBtnCls,
                  computed.isMasterSolutionRefining && 'opacity-50 cursor-not-allowed',
                )}
              >
                <Pencil size={14} />
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={actions.handleCancelMasterSolution}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 bg-white dark:bg-secondary border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg transition-colors shadow-sm"
            >
              <X size={14} />
            </button>
            <button
              onClick={actions.handleSaveMasterSolution}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white border border-transparent rounded-lg transition-colors shadow-sm',
                saveBtnCls,
              )}
            >
              <Check size={14} />
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 p-0 overflow-hidden relative">
        {computed.isMasterSolutionRefining && (
          <BoxLoadingOverlay
            label={t('Canvas.msgs.masterSolutionRefining', { defaultValue: 'Updating master solution…' })}
          />
        )}
        {state.isEditingMasterSolution ? (
          <LatexRichEditor
            value={state.masterSolutionDraft}
            onChange={actions.setMasterSolutionDraft}
            placeholder={t('Canvas.msgs.enterMasterSolution')}
            className="h-full"
            examId={state.examId}
          />
        ) : (
          <div className="w-full h-full p-5 overflow-y-auto text-slate-800 dark:text-slate-200 text-sm leading-relaxed custom-scrollbar bg-white dark:bg-card">
            <SectionContent
              contentType={currentTask.masterSolutionContentType}
              imageUrls={state.resolvedMasterSolutionImageUrls}
              imageIds={state.masterSolutionImageIds}
              onDeleteImage={(imageIds, index) =>
                actions.handleDeleteSolutionImage('masterSolution', imageIds[index] || '')
              }
              onDropImages={files => actions.handleAddSolutionImages('masterSolution', files)}
              isUploading={state.uploadingImageType === 'masterSolution'}
              text={currentTask.masterSolution}
              emptyLabel={t('Canvas.msgs.noMasterSolutionYet')}
              imageAlt={t('Canvas.msgs.masterSolution')}
              proseClassName="prose prose-sm prose-blue dark:prose-invert max-w-none w-full"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Popover opened from a language-use group's badge in GradingSchemePanel: lets a teacher fix up which
 * tasks belong to that shared LanguageUseScheme group after the fact (split/merge). Edits are
 * draft-only until Save — see useSolutionState's openGroupMembershipEditor/saveGroupMembership.
 */
function LanguageUseGroupMembershipPicker({ store }: { store: SolutionStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions } = store;
  const draft = state.groupMembershipDraft;
  const saveDisabled = !draft || draft.size === 0 || state.isSavingGroupMembership;
  const popoverRef = useRef<HTMLDivElement>(null);

  // The dropdown opens flush with the group label's left edge, but must never cross the right edge of
  // the grading-scheme text it sits over — capped here by measuring that real boundary, not a guessed
  // width, so it stays correct at any panel width.
  useEffect(() => {
    const popover = popoverRef.current;
    const scrollArea = popover?.closest<HTMLElement>('[data-gs-scroll-area]');
    if (!popover || !scrollArea) return;
    const updateMaxWidth = () => {
      const style = getComputedStyle(scrollArea);
      const rightBound = scrollArea.getBoundingClientRect().right - parseFloat(style.paddingRight);
      const leftEdge = popover.getBoundingClientRect().left;
      popover.style.maxWidth = `${Math.max(0, rightBound - leftEdge)}px`;
    };
    updateMaxWidth();
    const observer = new ResizeObserver(updateMaxWidth);
    observer.observe(scrollArea);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={popoverRef}
      onPointerDown={event => event.stopPropagation()}
      className="absolute left-0 top-full mt-2 z-50 w-72 rounded-xl border-2 border-slate-100 dark:border-slate-700 bg-white dark:bg-card shadow-lg flex flex-col"
    >
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 dark:border-slate-700">
        <span className="min-w-0 break-words text-xs font-semibold text-slate-700 dark:text-slate-300">
          {t('Canvas.grading.editLanguageUseGroupTasks')}
        </span>
        <div className="relative group ml-auto">
          <Info size={13} className="text-slate-400" />
          <div className="absolute top-full mt-1.5 right-0 hidden group-hover:block w-56 px-2.5 py-1.5 text-[11px] font-medium text-white bg-slate-800 rounded-md shadow-lg z-50">
            {t('Canvas.grading.languageUseGroupPickerInfo')}
          </div>
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto py-1">
        {computed.groupMembershipPickerTasks.length === 0 ? (
          <div className="px-3 py-2 text-xs text-slate-400 italic">{t('Canvas.msgs.noTasksFound')}</div>
        ) : (
          computed.groupMembershipPickerTasks.map(task => {
            const isEligible = !!task.taskType && GRAMMAR_TASK_TYPES.has(task.taskType);
            const isChecked = !!task.id && !!draft?.has(task.id);
            return (
              <label
                key={task.id}
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={!task.id}
                  onChange={() => task.id && actions.toggleGroupMembershipDraft(task.id)}
                  className="h-3.5 w-3.5 cursor-pointer accent-violet-600"
                />
                <span
                  className={cn(
                    'text-xs',
                    isEligible ? 'text-slate-700 dark:text-slate-300' : 'text-slate-500 dark:text-slate-400 italic',
                  )}
                >
                  {renderTaskMainLabel(task.taskNumber || task.name, t)}
                </span>
              </label>
            );
          })
        )}
      </div>

      {state.groupMembershipError && (
        <div className="px-3 py-1.5 text-[11px] font-medium text-red-600 dark:text-red-400 border-t border-slate-100 dark:border-slate-700">
          {t('Canvas.grading.languageUseGroupPickerSaveFailed')}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-slate-100 dark:border-slate-700">
        <button
          type="button"
          onClick={() => actions.cancelGroupMembership()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 bg-white dark:bg-secondary border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
        >
          <X size={14} />
        </button>
        <div className="relative group">
          <button
            type="button"
            onClick={() => actions.saveGroupMembership()}
            disabled={saveDisabled}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white border border-transparent rounded-lg transition-colors',
              saveDisabled ? 'bg-slate-300 dark:bg-slate-600 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700',
            )}
          >
            {state.isSavingGroupMembership ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          </button>
          {draft && draft.size === 0 && (
            <div className="absolute top-full mt-2 right-0 hidden group-hover:block w-max max-w-64 px-2.5 py-1 text-xs font-medium text-white bg-slate-800 rounded-md shadow-lg z-50">
              {t('Canvas.grading.languageUseGroupPickerEmptyDraft')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function GradingSchemePanel({ store }: { store: SolutionStateStore }) {
  const { t } = useTranslation();
  const { state, computed, actions, refs } = store;
  const currentTask = computed.currentTask;
  // Which review button (if any) the user just clicked while it was blocked — shows the "points must be
  // > 0" hint briefly instead of silently doing nothing.
  const [blockedReviewTarget, setBlockedReviewTarget] = useState<'content' | 'languageUse' | null>(null);
  // Same, for a blocked SAVE attempt while editing (see saveBlocked below).
  const [saveBlockedMessageVisible, setSaveBlockedMessageVisible] = useState(false);

  if (!currentTask) return null;

  const isAiGenerated = currentTask.gradingSchemeSource === 'generated';
  // An image-classified grading scheme has no text to edit — show the image only, uneditable.
  const isImageGradingScheme = !shouldShowOcrText(currentTask.gradingSchemeContentType);
  const isLanguageUseBox = state.boxMode === 'languageUse';
  const isGradingSchemeRefining = isLanguageUseBox
    ? computed.isGradingSchemeLanguageUseRefining
    : computed.isGradingSchemeContentRefining;
  // Language-use criteria has no bounding-box/image support today — always plain text.
  const displayCriteria = isLanguageUseBox
    ? (computed.activeLanguageUseGroup?.criteria ?? '')
    : currentTask.gradingCriteria || '';
  const displayMaxPoints = isLanguageUseBox ? computed.currentLanguageUseMaxScore : (currentTask.gradingMaxPoints ?? 0);
  // The group badge (with its edit-membership affordance) shows whenever the task has a language-use
  // rule at all — every rule is a LanguageUseScheme now (solo or multi-member). Reflects whichever
  // group the chevrons currently point at, not just the first — a task can belong to more than one.
  const groupBoxSource = computed.activeLanguageUseSource;
  const groupIndex = groupBoxSource
    ? computed.languageUseBoxSources.findIndex(source => source.key === groupBoxSource.key)
    : -1;
  const hasMultipleGroups = computed.languageUseBoxSources.length > 1;
  // The panel's whole color scheme (border/header/icon/buttons) reflects the WORSE of content and
  // language-use confidence, regardless of which box mode is currently selected — a single-mode
  // signal was too easy to miss when the other mode was the one with low confidence. Stays red/amber
  // until both are resolved (better confidence or approved).
  const shouldColorConfidence = currentTask.gradingSchemeSource === 'upload';
  const contentStatus = state.gradingSchemeConfidenceStatusMap?.[computed.actualIndex];
  const languageUseStatus = state.languageUseConfidenceStatusMap?.[computed.actualIndex];
  const hasLanguageUseSection = computed.languageUseBoxSources.length > 0;
  // Zero own points (content or language-use) blocks grading start regardless of source or approval
  // (see ownMaxPoints/evaluateGradingSchemeComplete) — must colour the panel and block the review
  // button even for generated content, which shouldColorConfidence otherwise excludes.
  const contentZeroPoints = hasZeroContentPoints(currentTask);
  const languageUseZeroPoints = hasLanguageUseSection && computed.currentLanguageUseMaxScore <= 0;
  const panelStatus =
    contentZeroPoints || languageUseZeroPoints
      ? 'low'
      : shouldColorConfidence
        ? contentStatus === 'low' || languageUseStatus === 'low'
          ? 'low'
          : contentStatus === 'medium' || languageUseStatus === 'medium'
            ? 'medium'
            : undefined
        : undefined;
  const panelShellCls =
    panelStatus === 'low'
      ? 'bg-red-50/40 border-red-400'
      : panelStatus === 'medium'
        ? 'bg-amber-50/40 border-amber-300'
        : 'bg-white dark:bg-card border-slate-100 dark:border-slate-700';
  const panelHeaderCls =
    panelStatus === 'low'
      ? 'bg-red-100/70 border-red-200'
      : panelStatus === 'medium'
        ? 'bg-amber-100/70 border-amber-200'
        : 'bg-violet-50/50 dark:bg-violet-900/10 border-violet-100 dark:border-violet-900';
  const panelTitleCls =
    panelStatus === 'low'
      ? 'text-red-700 dark:text-red-400'
      : panelStatus === 'medium'
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-violet-800 dark:text-violet-400';
  const iconCls =
    panelStatus === 'low' ? 'text-red-600' : panelStatus === 'medium' ? 'text-amber-600' : 'text-violet-700';
  const pencilBtnCls =
    panelStatus === 'low'
      ? 'text-red-700 dark:text-red-400 bg-white dark:bg-secondary border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300'
      : panelStatus === 'medium'
        ? 'text-amber-700 dark:text-amber-400 bg-white dark:bg-secondary border border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300'
        : 'text-violet-700 dark:text-violet-400 bg-white dark:bg-secondary border border-violet-200 dark:border-violet-800 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:border-violet-300';
  const saveBtnCls =
    panelStatus === 'low'
      ? 'bg-red-600 hover:bg-red-700'
      : panelStatus === 'medium'
        ? 'bg-amber-600 hover:bg-amber-700'
        : 'bg-violet-600 hover:bg-violet-700';
  const badgeCls =
    panelStatus === 'low'
      ? 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-900'
      : panelStatus === 'medium'
        ? 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-900'
        : 'text-violet-700 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 border-violet-100 dark:border-violet-900';

  // Zero points can't be approved away (see contentZeroPoints/languageUseZeroPoints above) — the button
  // stays visible so it's clear a review is still expected, but greyed out and inert instead of silently no-op'ing.
  const reviewButtonCls = (status: 'low' | 'medium' | undefined, blocked: boolean) =>
    cn(
      'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-sm',
      blocked
        ? 'text-slate-400 dark:text-slate-600 bg-slate-50 dark:bg-secondary/40 border border-slate-200 dark:border-slate-700 cursor-not-allowed'
        : status === 'low'
          ? 'text-rose-700 dark:text-rose-400 bg-white dark:bg-secondary border border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/20'
          : 'text-amber-700 dark:text-amber-400 bg-white dark:bg-secondary border border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20',
    );

  // The review button follows whichever box mode is currently open — it must only ever act on the
  // section it visibly represents, not on whatever else happens to also be flagged.
  const activeReviewKind: 'content' | 'languageUse' = isLanguageUseBox ? 'languageUse' : 'content';
  const activeReviewStatus = isLanguageUseBox ? languageUseStatus : contentStatus;
  const activeReviewZeroPoints = isLanguageUseBox ? languageUseZeroPoints : contentZeroPoints;
  const activeReviewTarget: SaveTarget = isLanguageUseBox
    ? { kind: 'group', languageUseSchemeId: computed.activeLanguageUseSource?.key ?? '' }
    : { kind: 'content' };

  const handleReviewClick = (kind: 'content' | 'languageUse', target: SaveTarget, blocked: boolean) => {
    if (blocked) {
      setBlockedReviewTarget(kind);
      window.setTimeout(() => setBlockedReviewTarget(prev => (prev === kind ? null : prev)), 3000);
      return;
    }
    actions.handleApproveGradingScheme(target);
  };

  // Same rule as contentZeroPoints/languageUseZeroPoints, but against the DRAFT being edited rather
  // than the saved value — a task can't be saved into the same zero-points state that already blocks
  // approval.
  const activeMaxPointsDraft = isLanguageUseBox ? state.languageUseMaxPointsDraft : state.gradingMaxPointsDraft;
  const saveBlocked = (activeMaxPointsDraft ?? 0) <= 0;

  const handleSaveClick = () => {
    if (saveBlocked) {
      setSaveBlockedMessageVisible(true);
      window.setTimeout(() => setSaveBlockedMessageVisible(false), 3000);
      return;
    }
    actions.handleSaveGradingScheme();
  };

  return (
    <div className={cn('flex flex-col flex-1 min-h-0 rounded-xl border-2 shadow-sm overflow-hidden', panelShellCls)}>
      <div
        className={cn(
          '@container flex items-center justify-between px-1 h-11 border-b shrink-0 gap-0.5',
          panelHeaderCls,
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList size={18} className={cn(iconCls, 'shrink-0')} />
          <h3
            className={cn(
              'min-w-0 font-semibold overflow-hidden text-ellipsis whitespace-nowrap text-[clamp(9px,3.5cqw,14px)] leading-[clamp(10px,5cqw,20px)]',
              panelTitleCls,
            )}
          >
            {t('Canvas.msgs.gradingScheme')}
          </h3>
          <div className="min-w-0">
            <BoxModeToggle
              boxMode={state.boxMode}
              onChangeBoxMode={actions.setBoxMode}
              sources={computed.languageUseBoxSources}
              contentDotLevel={computed.contentConfidenceDotLevel}
              languageUseDotLevel={computed.languageUseConfidenceDotLevel}
            />
          </div>

          {isAiGenerated && (
            <div
              title={t('Canvas.msgs.aiGeneratedProposalTooltip')}
              className="flex items-center justify-center bg-violet-100 p-1 rounded-full text-violet-700 shadow-sm shrink-0"
            >
              <Sparkles size={14} />
              <div className="absolute bottom-full mb-2 hidden group-hover:block w-max px-2.5 py-1 text-xs font-medium text-white bg-slate-800 rounded-md shadow-lg z-50 animate-in fade-in zoom-in-95 duration-150">
                {t('Canvas.msgs.aiGeneratedProposal')}
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
              </div>
            </div>
          )}
        </div>
        {!state.isEditingGradingScheme ? (
          <div className="flex items-center gap-2 shrink-0">
            {/* ONE button, scoped to whichever box mode is currently open — it must only ever act on the
                section it visibly represents. The panel's border/header colour above stays blended (worst
                of content+languageUse) on purpose; only this button's presence/target follows boxMode. */}
            {(activeReviewStatus === 'low' || activeReviewStatus === 'medium') && (
              <div className="relative group">
                <button
                  onClick={() => handleReviewClick(activeReviewKind, activeReviewTarget, activeReviewZeroPoints)}
                  disabled={isGradingSchemeRefining}
                  className={reviewButtonCls(activeReviewStatus, activeReviewZeroPoints || isGradingSchemeRefining)}
                >
                  {activeReviewStatus === 'low' ? <AlertOctagon size={14} /> : <AlertTriangle size={14} />}
                  <span className="hidden @[21rem]:inline">{t('Canvas.msgs.review')}</span>
                </button>
                {/* CSS-only hover tooltip (no native `title` delay) so the reason shows immediately, not
                    after the browser's default hover pause. Placed BELOW the button — above sat right
                    under the cursor and was effectively invisible. Right-aligned to the button's own
                    right edge rather than centered/left-aligned. */}
                {activeReviewZeroPoints && (
                  <div className="absolute top-full mt-2 right-0 hidden group-hover:block w-max max-w-64 px-2.5 py-1 text-xs font-medium text-white bg-slate-800 rounded-md shadow-lg z-50">
                    {t('Canvas.msgs.approvalRequiresPoints')}
                  </div>
                )}
                {blockedReviewTarget === activeReviewKind && (
                  <div className="absolute top-full mt-1 right-0 z-50 whitespace-nowrap px-2 py-1 text-[11px] font-medium text-white bg-slate-800 rounded-md shadow-lg">
                    {t('Canvas.msgs.approvalRequiresPoints')}
                  </div>
                )}
              </div>
            )}
            {/* Edit stays available even for an image scheme so maxPoints remains editable;
                only the criteria text is locked (the image is shown instead). */}
            <button
              onClick={() => actions.setIsEditingGradingScheme(true)}
              disabled={isGradingSchemeRefining}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-sm',
                pencilBtnCls,
                isGradingSchemeRefining && 'opacity-50 cursor-not-allowed',
              )}
            >
              <Pencil size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={actions.handleCancelGradingScheme}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 bg-white dark:bg-secondary border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg transition-colors shadow-sm"
            >
              <X size={14} />
            </button>
            <div className="relative group">
              <button
                onClick={handleSaveClick}
                disabled={state.isCalculatingMaxPoints}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-transparent rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed',
                  saveBlocked
                    ? 'text-slate-400 dark:text-slate-600 bg-slate-100 dark:bg-secondary/40 cursor-not-allowed'
                    : cn('text-white', saveBtnCls),
                )}
              >
                {state.isCalculatingMaxPoints ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              </button>
              {saveBlocked && (
                <div className="absolute top-full mt-2 right-0 hidden group-hover:block w-max max-w-64 px-2.5 py-1 text-xs font-medium text-white bg-slate-800 rounded-md shadow-lg z-50">
                  {t('Canvas.msgs.saveRequiresPoints')}
                </div>
              )}
              {saveBlockedMessageVisible && (
                <div className="absolute top-full mt-1 right-0 z-50 whitespace-nowrap px-2 py-1 text-[11px] font-medium text-white bg-slate-800 rounded-md shadow-lg">
                  {t('Canvas.msgs.saveRequiresPoints')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-hidden relative bg-white dark:bg-card">
        {isGradingSchemeRefining && (
          <BoxLoadingOverlay
            label={t('Canvas.msgs.gradingSchemeRefining', { defaultValue: 'Updating grading scheme…' })}
          />
        )}
        {state.isEditingGradingScheme ? (
          isLanguageUseBox ? (
            <div className="p-5 overflow-y-auto h-full custom-scrollbar flex flex-col gap-3">
              <div className="flex items-center gap-2 shrink-0">
                <label className="text-xs font-medium text-slate-600">{t('Canvas.msgs.maxPoints')}</label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={state.languageUseMaxPointsDraft}
                    onChange={e => actions.handleLanguageUseMaxPointsChange(e.target.value)}
                    disabled={state.isCalculatingMaxPoints}
                    className="w-24 px-2 py-1.5 text-sm border border-violet-200 rounded-md outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50"
                  />
                  {state.isCalculatingMaxPoints && (
                    <Loader2 size={14} className="absolute right-2 animate-spin text-violet-400" />
                  )}
                </div>
              </div>
              <LatexRichEditor
                value={state.languageUseCriteriaDraft}
                onChange={actions.handleLanguageUseCriteriaChange}
                className="w-full flex-1 min-h-[140px] border-violet-200 focus-within:ring-2 focus-within:ring-violet-300"
                placeholder={t('Canvas.msgs.enterGradingScheme')}
                examId={state.examId}
              />
            </div>
          ) : (
            <div className="p-5 overflow-y-auto h-full custom-scrollbar flex flex-col gap-3">
              <div className="flex items-center gap-2 shrink-0">
                <label className="text-xs font-medium text-slate-600">{t('Canvas.msgs.maxPoints')}</label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={state.gradingMaxPointsDraft}
                    onChange={e => actions.handleGradingMaxPointsChange(e.target.value)}
                    disabled={state.isCalculatingMaxPoints}
                    className="w-24 px-2 py-1.5 text-sm border border-violet-200 rounded-md outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50"
                  />
                  {state.isCalculatingMaxPoints && (
                    <Loader2 size={14} className="absolute right-2 animate-spin text-violet-400" />
                  )}
                </div>
              </div>
              {isImageGradingScheme ? (
                // Criteria is an image — keep it visible (uneditable) while maxPoints stays editable above.
                <div className="flex-1 overflow-auto p-2">
                  <SectionContent
                    contentType={currentTask.gradingSchemeContentType}
                    imageUrls={state.resolvedGradingSchemeImageUrls}
                    imageIds={state.gradingSchemeImageIds}
                    text={currentTask.gradingCriteria || ''}
                    emptyLabel={t('Canvas.msgs.noImageAvailable')}
                    imageAlt={t('Canvas.msgs.gradingScheme')}
                    proseClassName="prose prose-sm prose-violet max-w-none w-full"
                    onDeleteImage={(imageIds, index) =>
                      actions.handleDeleteSolutionImage('gradingScheme', imageIds[index] || '')
                    }
                    onDropImages={files => actions.handleAddSolutionImages('gradingScheme', files)}
                    isUploading={state.uploadingImageType === 'gradingScheme'}
                  />
                </div>
              ) : (
                <LatexRichEditor
                  value={state.gradingCriteriaDraft}
                  onChange={actions.handleGradingCriteriaChange}
                  className="w-full flex-1 min-h-[140px] border-violet-200 focus-within:ring-2 focus-within:ring-violet-300"
                  placeholder={t('Canvas.msgs.enterGradingScheme')}
                  examId={state.examId}
                />
              )}
            </div>
          )
        ) : (
          <div
            data-gs-scroll-area
            className="w-full h-full p-5 overflow-y-auto text-slate-800 dark:text-slate-200 text-sm leading-relaxed custom-scrollbar"
          >
            <div className="mb-4 shrink-0 flex items-center gap-2 flex-wrap">
              <span className={cn('text-xs font-medium inline-block px-2 py-1 rounded-md border', badgeCls)}>
                {t('Canvas.msgs.maxPointsValue', { points: displayMaxPoints })}
              </span>
              {isLanguageUseBox && groupBoxSource && (
                <div
                  className="relative inline-flex items-center gap-0.5 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 transition-colors"
                  ref={refs.groupPickerRef}
                >
                  {hasMultipleGroups && (
                    <button
                      type="button"
                      disabled={groupIndex <= 0}
                      onClick={() =>
                        actions.setActiveLanguageUseSourceKey(computed.languageUseBoxSources[groupIndex - 1]!.key)
                      }
                      className="p-1 pl-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                    >
                      <ChevronLeft size={12} />
                    </button>
                  )}
                  <span className={cn('text-[11px] font-medium py-1', hasMultipleGroups ? 'px-1' : 'pl-2 pr-1')}>
                    {groupBoxSource.displayNumber != null
                      ? t('Canvas.grading.groupLanguageUseRuleNumbered', {
                          number: groupBoxSource.displayNumber,
                          tasks: groupBoxSource.dependsOn.map(formatTaskReferenceForDisplay).join(', '),
                        })
                      : t('Canvas.grading.groupLanguageUseRule', {
                          tasks: groupBoxSource.dependsOn.map(formatTaskReferenceForDisplay).join(', '),
                        })}
                  </span>
                  <button
                    type="button"
                    onClick={() => actions.openGroupMembershipEditor()}
                    title={t('Canvas.grading.editLanguageUseGroupTasks')}
                    className="p-1 hover:opacity-70"
                  >
                    <Pencil size={10} />
                  </button>
                  {state.isGroupPickerOpen && <LanguageUseGroupMembershipPicker store={store} />}
                  <div className="relative group">
                    <button
                      type="button"
                      disabled={computed.isGradingInProgress}
                      onClick={() => {
                        if (window.confirm(t('Canvas.grading.deleteLanguageUseGroupConfirm')))
                          actions.deleteLanguageUseGroup();
                      }}
                      title={computed.isGradingInProgress ? undefined : t('Canvas.grading.deleteLanguageUseGroup')}
                      className={cn(
                        'p-1',
                        hasMultipleGroups ? '' : 'pr-2',
                        computed.isGradingInProgress
                          ? 'opacity-40 cursor-not-allowed'
                          : 'hover:opacity-70 hover:text-destructive',
                      )}
                    >
                      <Trash2 size={10} />
                    </button>
                    {computed.isGradingInProgress && (
                      <div className="absolute top-full mt-2 right-0 hidden group-hover:block w-max max-w-64 px-2.5 py-1 text-xs font-medium text-white bg-slate-800 rounded-md shadow-lg z-50">
                        {t('Canvas.grading.deleteLanguageUseGroupBlocked')}
                      </div>
                    )}
                    {state.deleteLanguageUseGroupError && (
                      <div className="absolute top-full mt-2 right-0 w-max max-w-64 px-2.5 py-1 text-xs font-medium text-white bg-red-600 rounded-md shadow-lg z-50">
                        {state.deleteLanguageUseGroupError}
                      </div>
                    )}
                  </div>
                  {hasMultipleGroups && (
                    <button
                      type="button"
                      disabled={groupIndex === -1 || groupIndex >= computed.languageUseBoxSources.length - 1}
                      onClick={() =>
                        actions.setActiveLanguageUseSourceKey(computed.languageUseBoxSources[groupIndex + 1]!.key)
                      }
                      className="p-1 pr-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                    >
                      <ChevronRight size={12} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {isLanguageUseBox ? (
              displayCriteria ? (
                <div className="prose prose-sm prose-violet max-w-none w-full dark:prose-invert">
                  <MarkdownWithMath>{displayCriteria}</MarkdownWithMath>
                </div>
              ) : (
                <span className="text-slate-400 italic">{t('Canvas.msgs.noGradingSchemeYet')}</span>
              )
            ) : (
              <SectionContent
                contentType={currentTask.gradingSchemeContentType}
                imageUrls={state.resolvedGradingSchemeImageUrls}
                imageIds={state.gradingSchemeImageIds}
                text={currentTask.gradingCriteria || ''}
                emptyLabel={t('Canvas.msgs.noGradingSchemeYet')}
                imageAlt={t('Canvas.msgs.gradingScheme')}
                proseClassName="prose prose-sm prose-violet max-w-none w-full"
                onDeleteImage={(imageIds, index) =>
                  actions.handleDeleteSolutionImage('gradingScheme', imageIds[index] || '')
                }
                onDropImages={files => actions.handleAddSolutionImages('gradingScheme', files)}
                isUploading={state.uploadingImageType === 'gradingScheme'}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
